import * as React from 'react';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { listUsers, updateUserPermissions } from '@/api/client';
import { showToast } from '@/components/ui/toast';
import { MODULES, MODULE_GROUPS, DEFAULT_MODULES, DEFAULT_CHANNELS } from '@/lib/modules';
import type { ModuleGroup } from '@/lib/modules';

type ManagedUser = {
  id: number;
  username: string;
  display_name: string;
  role: string;
  status: string;
  permissions: any;
};

const PLATFORMS = [
  { key: 'douyin', short: '抖音' },
  { key: 'xiaohongshu', short: '小红书' },
  { key: 'wechat', short: '视频号' },
];

type UserEdit = {
  channels: Record<string, { max: number }>;
  modules: Record<string, boolean>;
  original: string; // 规范化权限 JSON，用于 dirty 判定
};

const serialize = (channels: Record<string, { max: number }>, modules: Record<string, boolean>) =>
  JSON.stringify({ ...channels, modules });

export function PermissionsTab() {
  const [users, setUsers] = React.useState<ManagedUser[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [edits, setEdits] = React.useState<Record<number, UserEdit>>({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data.users || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // 解析用户权限并补全默认值（兼容旧版 channels 嵌套格式）
  const getPerms = React.useCallback((u: ManagedUser) => {
    const p = (typeof u.permissions === 'string' ? JSON.parse(u.permissions || '{}') : u.permissions) || {};
    const ch = p.channels && typeof p.channels === 'object' && !Array.isArray(p.channels) ? p.channels : p;
    const channels: Record<string, { max: number }> = {};
    for (const pf of PLATFORMS) {
      channels[pf.key] = { max: ch[pf.key]?.max ?? DEFAULT_CHANNELS[pf.key].max };
    }
    const modules: Record<string, boolean> = { ...DEFAULT_MODULES, ...(p.modules || {}) };
    return { channels, modules };
  }, []);

  const initEdit = React.useCallback((u: ManagedUser) => {
    const p = getPerms(u);
    setEdits((prev) => ({
      ...prev,
      [u.id]: {
        channels: { ...p.channels },
        modules: { ...p.modules },
        original: serialize(p.channels, p.modules),
      },
    }));
  }, [getPerms]);

  React.useEffect(() => {
    if (!loading && users.length > 0) {
      for (const u of users) {
        if (!edits[u.id]) initEdit(u);
      }
    }
  }, [loading, users, edits, initEdit]);

  const isDirty = React.useCallback((u: ManagedUser) => {
    const edit = edits[u.id];
    if (!edit) return false;
    return serialize(edit.channels, edit.modules) !== edit.original;
  }, [edits]);

  const dirtyCount = users.filter((u) => u.role !== 'admin' && isDirty(u)).length;

  const toggleCell = React.useCallback((userId: number, modKey: string) => {
    setEdits((prev) => {
      const edit = prev[userId];
      if (!edit) return prev;
      return {
        ...prev,
        [userId]: {
          ...edit,
          modules: { ...edit.modules, [modKey]: !(edit.modules[modKey] !== false) },
        },
      };
    });
  }, []);

  // 表头整列批量开/关：该列任一用户开着 → 全部关闭；全部关闭 → 全部开启
  const toggleColumn = React.useCallback((modKey: string) => {
    setEdits((prev) => {
      const next = { ...prev };
      const targets = users.filter((u) => u.role !== 'admin' && next[u.id]);
      const anyOn = targets.some((u) => next[u.id].modules[modKey] !== false);
      for (const u of targets) {
        next[u.id] = { ...next[u.id], modules: { ...next[u.id].modules, [modKey]: !anyOn } };
      }
      return next;
    });
  }, [users]);

  const handleChannelChange = React.useCallback((userId: number, platform: string, value: number) => {
    setEdits((prev) => {
      const edit = prev[userId];
      if (!edit) return prev;
      return {
        ...prev,
        [userId]: { ...edit, channels: { ...edit.channels, [platform]: { max: Math.max(0, value) } } },
      };
    });
  }, []);

  const saveAll = React.useCallback(async () => {
    const targets = users.filter((u) => u.role !== 'admin' && isDirty(u));
    if (targets.length === 0) return;
    setSaving(true);
    let ok = 0;
    const failed: string[] = [];
    for (const u of targets) {
      const edit = edits[u.id];
      const permData = { ...edit.channels, modules: edit.modules };
      try {
        await updateUserPermissions(u.username, permData);
        setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, permissions: JSON.stringify(permData) } : x));
        setEdits((prev) => ({ ...prev, [u.id]: { ...prev[u.id], original: serialize(edit.channels, edit.modules) } }));
        ok += 1;
      } catch (err) {
        failed.push(u.username);
        console.error('保存失败', u.username, err);
      }
    }
    setSaving(false);
    if (failed.length === 0) {
      showToast(`已保存 ${ok} 个用户的设置`, 'success');
    } else {
      showToast(`保存失败：${failed.join('、')}（共 ${failed.length} 个）`, 'error', 6000);
    }
  }, [users, edits, isDirty]);

  const discardAll = React.useCallback(() => {
    for (const u of users) {
      if (u.role !== 'admin' && edits[u.id]) initEdit(u);
    }
    showToast('已撤销未保存的改动', 'success');
  }, [users, edits, initEdit]);

  const groupModules = (g: ModuleGroup) => MODULES.filter((m) => m.group === g);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>模块可见性与监控配额</CardTitle>
            <CardDescription>表格内直接修改，点表头模块名可对全部用户批量开/关，最后统一保存</CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={load} title="刷新">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      {/* 操作栏 */}
      <div className="flex items-center justify-between border-y px-4 py-2.5">
        <div className="text-sm">
          {dirtyCount > 0
            ? <span className="font-medium text-foreground">已改动 {dirtyCount} 处</span>
            : <span className="text-muted-foreground">暂无未保存的改动</span>}
          <span className="ml-3 text-xs text-muted-foreground/70">点表头模块名可批量开/关</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={dirtyCount === 0 || saving} onClick={discardAll}>
            撤销改动
          </Button>
          <Button size="sm" disabled={dirtyCount === 0 || saving} onClick={saveAll}>
            {saving ? '保存中…' : '保存全部'}
          </Button>
        </div>
      </div>

      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-20 w-36 border-b border-r bg-background px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    用户
                  </th>
                  {MODULE_GROUPS.map((g) => (
                    <th
                      key={g}
                      colSpan={groupModules(g).length}
                      className="border-b border-r bg-background px-2 py-1.5 text-center text-xs font-medium text-muted-foreground"
                    >
                      {g}
                    </th>
                  ))}
                  <th
                    colSpan={PLATFORMS.length}
                    className="border-b bg-background px-2 py-1.5 text-center text-xs font-medium text-muted-foreground"
                  >
                    监控配额
                  </th>
                </tr>
                <tr>
                  <th className="sticky left-0 z-20 border-b border-r bg-background" />
                  {MODULES.map((m) => (
                    <th
                      key={m.key}
                      onClick={() => toggleColumn(m.key)}
                      title="点击对全部用户批量开/关该模块"
                      className="h-9 cursor-pointer select-none border-b border-r bg-background px-2 text-center text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      {m.short}
                    </th>
                  ))}
                  {PLATFORMS.map((p) => (
                    <th
                      key={p.key}
                      className="h-9 border-b bg-background px-2 text-center text-xs font-medium text-muted-foreground"
                    >
                      {p.short}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const edit = edits[u.id];
                  const isAdmin = u.role === 'admin';
                  const dirty = isDirty(u);
                  return (
                    <tr key={u.id} className={isAdmin ? 'bg-muted/20' : dirty ? 'bg-primary/[0.04]' : ''}>
                      <td className="sticky left-0 z-10 border-b border-r bg-background px-3 py-2">
                        <div className="flex items-center gap-1">
                          <span className={`font-medium ${isAdmin ? 'text-primary' : ''}`}>{u.username}</span>
                          {isAdmin && <Badge variant="default" className="ml-1">管理员</Badge>}
                          {u.status === 'disabled' && <Badge variant="destructive">已禁用</Badge>}
                        </div>
                        <div className="max-w-[120px] truncate text-xs text-muted-foreground">{u.display_name || '—'}</div>
                      </td>
                      {MODULES.map((m) => (
                        <td key={m.key} className="border-b border-r px-1.5 py-1.5 text-center">
                          {isAdmin ? (
                            <span className="text-xs text-muted-foreground">全显</span>
                          ) : (
                            <button
                              onClick={() => toggleCell(u.id, m.key)}
                              className={`h-6 w-14 rounded text-xs font-medium transition-colors ${
                                edit?.modules?.[m.key] !== false
                                  ? 'bg-primary text-primary-foreground'
                                  : 'border border-border text-muted-foreground hover:border-muted-foreground'
                              }`}
                            >
                              {edit?.modules?.[m.key] !== false ? '开' : '关'}
                            </button>
                          )}
                        </td>
                      ))}
                      {PLATFORMS.map((p) => (
                        <td key={p.key} className="border-b px-1.5 py-1.5 text-center">
                          {isAdmin ? (
                            <span className="text-xs text-muted-foreground">999</span>
                          ) : (
                            <Input
                              type="number"
                              min={0}
                              max={999}
                              value={edit?.channels?.[p.key]?.max ?? 0}
                              onChange={(e) => handleChannelChange(u.id, p.key, parseInt(e.target.value) || 0)}
                              className="mx-auto h-7 w-16 px-1 text-center text-xs"
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">暂无用户</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
